import { useMemo } from "react";

import CustomFieldCell from "@/components/Evidence/CustomFieldCell";
import {
  CUSTOM_FIELD_CATEGORIES,
  CustomFieldsQuery,
  isCustomFieldApplicableToType,
} from "@/Features/CustomFields";
import { ASSET_TYPE_IDS } from "../labAssets.constants";
import type { LabAsset } from "../labAssets.types";
import {
  ALL_ASSET_TYPES,
  LabAssetColumn,
  LabAssetColumnDefinitions,
  LabAssetCustomColumn,
} from "./LabAssetColumnDefinitions";

// Its own module, not LabAssetColumnDefinitions.tsx: the CustomFields barrel
// pulls in the whole app (custom-fields.query → @/index), and the static
// defs are imported by code that should stay light (History captions).

/**
 * The static columns plus one hidden column per asset custom field. Every
 * column is API-sortable (`sort_by` accepts `custom_field_<id>` since
 * ENG-2458). A field's `type_ids` scope becomes its `assetTypes`. Mirrors
 * useStorageColumnDefinitions.
 */
export const useLabAssetColumnDefinitions = () => {
  const { data, isLoading } = CustomFieldsQuery.useGetCustomFields({
    category: CUSTOM_FIELD_CATEGORIES.ASSET,
  });

  // Memoized so the Table's columnProps keep a stable identity across renders.
  const columnDefinitions: (LabAssetColumn | LabAssetCustomColumn)[] =
    useMemo(() => {
      if (!data) return LabAssetColumnDefinitions;
      return [
        ...LabAssetColumnDefinitions,
        ...data.data.map(
          (field): LabAssetCustomColumn => ({
            dataField: `custom_field_${field.field_id}`,
            caption: field.field_name,
            visible: false,
            assetTypes: ALL_ASSET_TYPES.filter((type) =>
              isCustomFieldApplicableToType(field, ASSET_TYPE_IDS[type])
            ),
            // null (not an empty cell element) when there's no stored value,
            // so DetailsDisplay's hideEmptyFields can drop it.
            render: ({ rowData }: { rowData: LabAsset }) =>
              rowData.custom_fields?.some(
                (attr) => attr.field_id === field.field_id
              ) ? (
                <CustomFieldCell
                  rowData={{ custom_attributes: rowData.custom_fields }}
                  field={field}
                />
              ) : null,
          })
        ),
      ];
    }, [data]);

  return { columnDefinitions, isLoading };
};
